/**
 * Which Claude account each login is signed in to, for the Logins list:
 * a short account line, and which other login already uses the same account.
 */
import { isCliLoginSignedIn, type CliLoginStatus } from "@/desktop/cli-logins";

/** Organisation plans, where the organisation name tells accounts apart. */
const ORG_PLANS = new Set(["team", "enterprise"]);

/** "a@b.com · Max", or `null` while the account is unknown. */
export function accountSummary(
  status: CliLoginStatus | null | undefined,
): string | null {
  if (!status?.logged_in || !status.email) return null;
  const plan = status.subscription_type?.trim().toLowerCase() ?? "";
  const parts = [status.email];
  if (plan) parts.push(plan.charAt(0).toUpperCase() + plan.slice(1));
  if (ORG_PLANS.has(plan) && status.org_name) parts.push(status.org_name);
  return parts.join(" · ");
}

export interface LoginAccount {
  /** `null` is the default login. */
  id: string | null;
  name: string;
  status: CliLoginStatus | null | undefined;
}

/**
 * The first other login signed in to the same account: the same email in
 * any case, in the same organisation (one email may bill two organisations).
 */
export function sameAccountAs(
  login: LoginAccount,
  others: readonly LoginAccount[],
): string | null {
  const account = accountKey(login.status);
  if (!account) return null;
  const match = others.find(
    (other) => other.id !== login.id && accountKey(other.status) === account,
  );
  return match?.name ?? null;
}

function accountKey(status: CliLoginStatus | null | undefined) {
  if (!status?.email || !isCliLoginSignedIn(status)) return null;
  const org = status.org_name?.trim().toLowerCase() ?? "";
  return `${status.email.trim().toLowerCase()}\n${org}`;
}
